import { Outlet, Link, createRootRouteWithContext, HeadContent, Scripts, useRouterState } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createIsomorphicFn } from "@tanstack/react-start";
import { lazy, Suspense, useEffect, useState } from "react";
import appCss from "../styles.css?url";

// Read the SSR request id via an isomorphic fn so the server-only
// `@tanstack/react-start/server` import never reaches the client bundle.
const readSsrRequestId = createIsomorphicFn()
  .client(() => "")
  .server(() => {
    try {
      // Lazy require keeps this out of the client bundle entirely.
      const { getRequest } = require("@tanstack/react-start/server") as typeof import("@tanstack/react-start/server");
      return getRequestId(getRequest());
    } catch {
      return "";
    }
  });
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useVisitorTracking } from "@/hooks/use-visitor-tracking";
import { getRequestId } from "@/lib/request-id";

// Lazy-loaded: not required for first paint. Cuts ~200+ KiB from initial JS.
const ChatWidget = lazy(() => import("@/components/ChatWidget").then((m) => ({ default: m.ChatWidget })));
const CompareBar = lazy(() => import("@/components/CompareBar").then((m) => ({ default: m.CompareBar })));
const Toaster = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));
const ClientErrorReporter = lazy(() => import("@/components/ClientErrorReporter").then((m) => ({ default: m.ClientErrorReporter })));
const CanonicalUrlValidator = lazy(() => import("@/components/CanonicalUrlValidator").then((m) => ({ default: m.CanonicalUrlValidator })));
const CookieConsentBanner = lazy(() => import("@/components/CookieConsent").then((m) => ({ default: m.CookieConsentBanner })));
const MetaPixel = lazy(() => import("@/components/MetaPixel").then((m) => ({ default: m.MetaPixel })));
const GoogleTag = lazy(() => import("@/components/GoogleTag").then((m) => ({ default: m.GoogleTag })));

interface RouterContext {
  queryClient: QueryClient;
}

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Siden blev ikke fundet</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Siden du leder efter eksisterer ikke eller er blevet flyttet.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Gå til forsiden
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#0b0f17" },
      { name: "author", content: "AutoXChange ApS" },
      { name: "format-detection", content: "telephone=yes" },
      // Sensible default — every shareable route overrides title/description/og:* via head().
      { title: "AutoXChange - Kvalitetsbiler til de bedste priser" },
      { name: "description", content: "Hos AutoXChange finder du et spændende udvalg af brugte kvalitetsbiler til landets bedste priser. Tryg og sikker bilhandel i Børkop." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "AutoXChange" },
      { property: "og:locale", content: "da_DK" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "google-site-verification", content: "PeOdlpFBvRWM1D0JMHYoX_QWoLyUXWJRhTChKDntJUo" },
      { name: "facebook-domain-verification", content: "g9alze8ubyw0e7wb6j7sf3qdbbqjx8" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      // Preconnect ONLY to origins on the critical path. We dropped:
      //   • fonts.googleapis.com / fonts.gstatic.com — Inter is now self-
      //     hosted via @fontsource-variable/inter (bundled woff2, no extra
      //     RTT, no render-blocking CSS request).
      //   • billeder.bilinfo.net — Lighthouse flagged this as an unused
      //     preconnect (we no longer hotlink Bilinfo CDN images on the home
      //     page; all car photos go through Supabase Storage now).
      // Keeping unused preconnects holds open sockets the browser could use
      // for the LCP image, so we trim aggressively.
      { rel: "preconnect", href: "https://tszohmrldhhdsasapbiz.supabase.co", crossOrigin: "anonymous" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  // SSR: read the correlation id the request-logger middleware tagged on
  // the inbound Request. Browser keeps the SSR-rendered value; we don't
  // re-derive on the client (no Request available there).
  const reqId = readSsrRequestId();
  return (
    <html lang="da-DK" data-req-id={reqId} suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const hideChat = pathname.startsWith("/admin") || pathname.startsWith("/login") || pathname.startsWith("/reset-password");
  const isAdminArea = pathname.startsWith("/admin") || pathname.startsWith("/login") || pathname.startsWith("/reset-password");
  const showCompareBar = !isAdminArea && !pathname.startsWith("/sammenlign");

  useVisitorTracking();

  // Defer non-critical UI (chat, pixels, toasts, validators) until after first
  // paint so they don't block LCP / inflate the initial JS bundle Lighthouse
  // measures. requestIdleCallback falls back to setTimeout on Safari.
  const [deferredReady, setDeferredReady] = useState(false);
  useEffect(() => {
    const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
    const schedule = w.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 1500));
    const id = schedule(() => setDeferredReady(true), { timeout: 3000 });
    return () => {
      const cancel = (window as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback;
      if (cancel) cancel(id);
      else window.clearTimeout(id);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Header />
      <main className="min-h-screen">
        <Outlet />
      </main>
      <Footer />
      {deferredReady && (
        <Suspense fallback={null}>
          {!hideChat && <ChatWidget />}
          {showCompareBar && <CompareBar />}
          {!isAdminArea && <CookieConsentBanner />}
          {!isAdminArea && <MetaPixel />}
          {!isAdminArea && <GoogleTag />}
          <Toaster richColors position="bottom-right" />
          <ClientErrorReporter />
          <CanonicalUrlValidator />
        </Suspense>
      )}
    </QueryClientProvider>
  );
}
